import dayjs from 'dayjs';
import type { Machine } from './types';
import type { Settings } from '../settings/types';
import { ENGINE_TYPES } from './constants';
import { DETAIL_PANEL_LABELS as LABELS } from '../../utils/labels';
import { formatAddressWithName } from '../../utils/formatAddress';

function fillTemplate(template: string, vars: Record<string, string>): string {
  const fillLine = (line: string) => line.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? '').trimEnd();
  return template
    .split('\n')
    // Only drop a line if it originally contained a {placeholder} and resolved to an
    // empty "Label:" — plain prose lines (e.g. an intro sentence ending in ':') must
    // never be treated as an empty field, so they're never checked against the filter.
    .filter(line => !/\{(\w+)\}/.test(line) || !/^[^:]+:\s*$/.test(fillLine(line)))
    .map(fillLine)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n');
}

function buildTemplateVars(machine: Machine, projectName: string): Record<string, string> {
  const fmtDate = (d?: string) => (d ? dayjs(d).format('DD/MM/YYYY') : '');
  const fmtTime = (t?: string) => (t ? dayjs(`2000-01-01T${t}`).format('HH:mm') : '');
  const a = machine.address;
  const address = a ? formatAddressWithName(a) : '';

  return {
    machine_name: machine.name,
    machine_number: machine.machine_number ?? '',
    machine_category: machine.category ?? '',
    engine_type: ENGINE_TYPES[machine.engine_type]?.label ?? machine.engine_type,
    options: machine.options?.join(', ') ?? '',
    project_name: projectName,
    start_date: fmtDate(machine.start_date),
    start_time: fmtTime(machine.start_time),
    end_date: fmtDate(machine.end_date),
    end_time: fmtTime(machine.end_time),
    pickup_on_end_date: machine.pickup_on_end_date ? 'Ja' : 'Nee',
    address,
    notes: machine.notes ?? '',
    supplier_name: machine.supplier?.name ?? '',
  };
}

function toMailHref(email: string, subject: string, body: string): string {
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function getOrderMailHref(
  machine: Machine,
  projectName: string,
  orgSettings: Pick<Settings, 'order_email_subject' | 'order_email_body'>
): string | null {
  const email = machine.supplier?.email;
  if (!email) return null;
  const vars = buildTemplateVars(machine, projectName);
  const subject = orgSettings.order_email_subject || LABELS.orderEmailSubject;
  const body = orgSettings.order_email_body || LABELS.orderEmailBody;
  return toMailHref(email, fillTemplate(subject, vars), fillTemplate(body, vars));
}

export function getReturnMailHref(
  machine: Machine,
  projectName: string,
  orgSettings: Pick<Settings, 'return_email_subject' | 'return_email_body'>
): string | null {
  const email = machine.supplier?.email;
  if (!email) return null;
  const vars = buildTemplateVars(machine, projectName);
  const subject = orgSettings.return_email_subject || LABELS.returnEmailSubject;
  const body = orgSettings.return_email_body || LABELS.returnEmailBody;
  return toMailHref(email, fillTemplate(subject, vars), fillTemplate(body, vars));
}
